import {
    Box,
    Flex,
    IconButton,
    Text,
    VStack,
    Tooltip,
    TooltipTrigger,
    TooltipPositioner,
    TooltipContent,
    TooltipArrow,
  } from "@chakra-ui/react";
  import {
    FiMenu,
    FiHome,
    FiUsers,
    FiSettings,
    FiUserCheck
  } from "react-icons/fi";
  import { motion } from "framer-motion";
  import { useRouter } from "@tanstack/react-router";
import LogoutButton from "./LogoutButton";
  
  const MotionBox = motion(Box);
  
  const menuItems = [
    { icon: FiHome, label: "Dashboard", path: "/" },
    { icon: FiUsers, label: "Users", path: "/users" },
    { icon: FiSettings, label: "Settings", path: "/settings" },
    { icon: FiUserCheck, label: "Leads", path: "/leads"}
  ];
  
  export interface SidebarProps {
    collapsed: boolean;
    onToggleCollapse: () => void;
  }
  
  const Sidebar = ({ collapsed, onToggleCollapse }: SidebarProps) => {
    const router = useRouter();
    const currentPath = router.state.location.pathname;
  
    return (
      <MotionBox
        as="nav"
        height="100vh"
        position="sticky"
        top={0}
        zIndex={1000}
        width={collapsed ? "72px" : "240px"}
        bg={collapsed ? "gray.900" : "gray.100"}
        backdropFilter="blur(12px)"
        borderRight="1px solid rgba(255, 255, 255, 0.1)"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        px={collapsed ? 2 : 3}
        py={4}
        boxShadow="0 4px 30px rgba(0, 0, 0, 0.1)"
        borderRadius="0px 10px 10px 0px"
      >
        <Box>
          <IconButton
            aria-label="Toggle Sidebar"
            onClick={onToggleCollapse}
            size="sm"
            variant="ghost"
            alignSelf={collapsed ? "center" : "flex-end"}
            mb={6}
          >
            <FiMenu />
          </IconButton>
  
          <VStack align="stretch" gap={1}>
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;
  
              return (
                <Tooltip.Root key={index}>
                  <TooltipTrigger asChild>
                    <Flex
                      align="center"
                      justify={collapsed ? "center" : "flex-start"}
                      px={collapsed ? 0 : 3}
                      py={2}
                      columnGap={3}
                      borderRadius="lg"
                      cursor="pointer"
                      _hover={{ bg: collapsed ? "gray.800" : "gray.200" }}
                      bg={isActive ? (collapsed ? "gray.700" : "gray.300") : "transparent"}
                      transition="all 0.2s ease"
                      onClick={() => router.navigate({ to: item.path })}
                    >
                      <Icon size={20} color={collapsed ? "white" : "black"} />
                      {!collapsed && (
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color="black"
                          whiteSpace="nowrap"
                        >
                          {item.label}
                        </Text>
                      )}
                    </Flex>
                  </TooltipTrigger>
  
                  {collapsed && (
                    <TooltipPositioner>
                      <TooltipContent>
                        <TooltipArrow />
                        {item.label}
                      </TooltipContent>
                    </TooltipPositioner>
                  )}
                </Tooltip.Root>
              );
            })}
          </VStack>
        </Box>
        {!collapsed && (
          <Box mt="auto" px={4} pb={4}>
          <LogoutButton/>
        </Box>
        )}
        <Box textAlign={collapsed ? "center" : "left"} px={2} pt={4}>
          <Text fontSize="xs" color={collapsed ? "whiteAlpha.600" : "gray.700"}>
            {collapsed ? "©" : "© 2025 LeadMgr"}
          </Text>
        </Box>
      </MotionBox>
    );
  };
  
  export default Sidebar;
  